import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

import { bsMath } from "@bridgesplit/utils";
import { useObjectToMap } from "@bridgesplit/ui";
import { OracleQuote, OracleQuoteInfo, UncertaintyType } from "@bridgesplit/abf-sdk";

import { USDC_GENESIS_LP_MINT, USDC_GENESIS_PRINCIPAL_MINT, USDC_GENESIS_TRUE_LP_PRICE } from "../constants";
import { useOracleHistoricalPricesQuery, useOraclePricesQuery } from "../reducers";
import { OHLCFilter, PriceFetchType } from "../types";

interface OraclePriceContextValue {
    prices: Record<string, OracleQuoteInfo> | undefined;
    priceMap: Map<string, OracleQuoteInfo> | undefined;
    isLoading: boolean;
    isError: boolean;
}

const OraclePriceContext = createContext<OraclePriceContextValue | undefined>(undefined);

/** Replaces the inflated oneUSDC quote. With no true price set the quote is dropped, so it shows no value. */
export function applyOneUsdcPriceOverride(prices: Record<string, OracleQuoteInfo> | undefined) {
    const quote = prices?.[USDC_GENESIS_LP_MINT];
    if (!prices || !quote) return prices;
    const { [USDC_GENESIS_LP_MINT]: _inflated, ...rest } = prices;
    if (USDC_GENESIS_TRUE_LP_PRICE === undefined) return rest;
    const usdPrice = USDC_GENESIS_TRUE_LP_PRICE * (prices[USDC_GENESIS_PRINCIPAL_MINT]?.spotPrice ?? 1);
    return {
        ...rest,
        [USDC_GENESIS_LP_MINT]: {
            ...quote,
            spotPrice: usdPrice,
            twapPrice: usdPrice,
            marketPrice: usdPrice,
            uncertainty: 0
        }
    };
}

export function OraclePriceProvider({ children }: { children: ReactNode }) {
    const { data: rawPrices, isLoading, isError } = useOraclePricesQuery(undefined);
    const prices = useMemo(() => applyOneUsdcPriceOverride(rawPrices), [rawPrices]);

    const priceMap = useObjectToMap(prices);

    const value = useMemo<OraclePriceContextValue>(
        () => ({
            prices,
            priceMap,
            isLoading: Boolean(isLoading && !prices),
            isError: Boolean(isError)
        }),
        [prices, priceMap, isLoading, isError]
    );

    return <OraclePriceContext.Provider value={value}>{children}</OraclePriceContext.Provider>;
}

export function useOraclePriceContext() {
    const context = useContext(OraclePriceContext);
    if (!context) {
        throw new Error("useOraclePriceContext must be used within an OraclePriceProvider");
    }

    return context;
}

export function useOraclePrices(
    mints: (string | undefined | null)[] | undefined,
    priceFetchType: PriceFetchType = PriceFetchType.Spot
) {
    const { priceMap, isLoading, isError } = useOraclePriceContext();
    const requestedMints = useMemo(() => mints?.filter((mint): mint is string => Boolean(mint)) ?? [], [mints]);

    const getOracle = useCallback(
        function getOracle(
            mint: string | undefined | null,
            priceType: PriceFetchType = priceFetchType
        ): OracleQuote | undefined {
            if (!priceMap || !mint) return undefined;
            const quote = priceMap.get(mint);
            if (!quote) return undefined;
            const spotPrice = quote.spotPrice;
            const twapPrice = quote.twapPrice;
            const usdPrice = priceType === PriceFetchType.Spot ? spotPrice : twapPrice;
            const uncertainty = quote.uncertainty;

            const baseQuotes: OracleQuoteInfo[] = [quote];

            return {
                uncertainty,
                oracleAccount: quote.oracleAccount,
                baseQuotes,
                usdPrice,
                marketPrice: quote.marketPrice ?? usdPrice,
                getUsdAmount: (...params) => getUsdAmountFromUsdPrice(usdPrice, uncertainty, ...params)
            };
        },
        [priceMap, priceFetchType]
    );

    const getUsdPrice = useCallback(
        function getUsdPrice(
            mint: string | undefined | null,
            uncertaintyType: UncertaintyType = UncertaintyType.Skip
        ): number | undefined {
            const oracle = getOracle(mint);
            if (!oracle) return undefined;
            const basePrice = oracle.usdPrice;
            if (!basePrice) return undefined;
            const uncertainty = oracle.uncertainty;
            if (uncertaintyType === UncertaintyType.Add) return basePrice + uncertainty;
            if (uncertaintyType === UncertaintyType.Subtract) return basePrice - uncertainty;

            return basePrice;
        },
        [getOracle]
    );

    const loading = isLoading || (!priceMap && requestedMints.length > 0);

    return { getOracle, getUsdPrice, isLoading: loading, isError };
}

export function useOracleHistoricalPrices(oracleHistoricalFilter: OHLCFilter) {
    return useOracleHistoricalPricesQuery(oracleHistoricalFilter, {
        skip: !oracleHistoricalFilter.mints.length
    });
}

export function getUsdAmountFromUsdPrice<T extends number | undefined>(
    usdPrice: number,
    uncertainty: number,
    amount: T,
    decimals?: number,
    uncertaintyType?: UncertaintyType
): T {
    if (uncertaintyType === UncertaintyType.Add) {
        usdPrice += uncertainty;
    } else if (uncertaintyType === UncertaintyType.Subtract) {
        usdPrice -= uncertainty;
    }
    if (decimals) {
        return bsMath.tokenAMul(amount, usdPrice, decimals) as T;
    }
    return bsMath.mul(amount, usdPrice) as T;
}

export function useAllAvailablePrices(mints: string[] | undefined) {
    const { getUsdPrice, isLoading: oracleLoading } = useOraclePrices(mints);

    const getPriceByMint = useCallback(
        (mint: string | undefined) => {
            return getUsdPrice(mint);
        },
        [getUsdPrice]
    );

    return { getPriceByMint, isLoading: oracleLoading };
}
